import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n';
import InstallAppPitch from '../InstallAppPitch';
import { useShouldPitchAppInstall } from '../../lib/useShouldPitchAppInstall';
import { useAskForPushPermission } from '../../lib/usePushPermissionAsk';
import { softGlow } from '../onboarding/glow';
import { ROW_BORDER, ROW_SURFACE } from '../onboarding/layout';

/**
 * The night, in the order it happens.
 *
 * These used to describe the machinery — we'll match you, you'll get an email,
 * check your dashboard — which told a booker what the system was going to do
 * and nothing about what they were going to do. Two of the four beats are now
 * theirs: say hello in the chat, and turn up. The last one is the whole point
 * of the product and had not previously appeared on the confirmation screen at
 * all.
 *
 * Each beat carries a body as well as a title. A four-line list answers "what
 * order does this happen in" and nothing else, which on a screen someone has
 * just paid on leaves the obvious questions (how big is the table? how do I
 * know where to go? what am I actually meant to do?) to be found elsewhere.
 * The bodies say the same things HowItWorksScreen says before the sale, in the
 * same voice, so nobody has to remember them from two screens ago.
 */
const STEPS = ['match', 'reveal', 'intro', 'show'] as const;

/**
 * Brand palette, rather than the five arbitrary hexes (two pinks and an amber
 * that appear nowhere else in the product) this used to scatter.
 */
const CONFETTI_COLORS = ['#EF6225', '#81358A', '#36A2AD', '#F89D7A'] as const;

/**
 * Eighteen, down from fifty. Fifty pieces of 8px round confetti reads as a
 * screensaver; a scatter you can count reads as a thing that happened.
 */
const CONFETTI_COUNT = 18;

/** Long enough for the slowest piece (0.45s delay + 3.1s fall) to land. */
const CONFETTI_LIFETIME_MS = 4000;

/**
 * The celebration, and now the only decoration on the screen.
 *
 * It stays — a booking confirmation is a transaction completing, which is the
 * one moment in the product that earns confetti (the onboarding welcome
 * deliberately does something quieter). What changed is how it is drawn.
 *
 * It falls the height of *this* element, measured, instead of
 * `window.innerHeight`: inside the booking modal the card is a fraction of the
 * viewport, so the old version spent most of its animation budget moving
 * pieces through an overflow-hidden box they had already left.
 *
 * Pieces are spread evenly across the width with a small jitter rather than
 * placed at fully random x. Fully random over eighteen pieces clumps often
 * enough to look like a bug, and the clumping was the reason the old one
 * needed fifty to look deliberate.
 */
function Confetti() {
  const layer = useRef<HTMLDivElement>(null);
  const [fall, setFall] = useState(0);

  useLayoutEffect(() => {
    const el = layer.current;
    if (el) setFall(el.getBoundingClientRect().height + 80);
  }, []);

  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        left: `${(i * 100) / CONFETTI_COUNT + Math.random() * 4}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: Math.random() * 0.45,
        duration: 1.9 + Math.random() * 1.2,
        rotate: 220 + Math.random() * 320,
        drift: -24 + Math.random() * 48,
      })),
    [],
  );

  return (
    <div
      ref={layer}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {fall > 0 &&
        pieces.map((p, i) => (
          <motion.span
            key={i}
            // Slivers of paper, not dots. Same silhouette as real confetti,
            // and it gives the rotation something to read against.
            className="absolute top-0 block w-[5px] h-[11px] rounded-[1px]"
            style={{ left: p.left, backgroundColor: p.color }}
            initial={{ y: -24, x: 0, rotate: 0, opacity: 1 }}
            animate={{ y: fall, x: p.drift, rotate: p.rotate, opacity: 0 }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: 'linear',
              // Held opaque for most of the fall, then out — pieces that fade
              // from the first frame read as a fog rather than as paper.
              opacity: {
                delay: p.delay + p.duration * 0.7,
                duration: p.duration * 0.3,
              },
            }}
          />
        ))}
    </div>
  );
}

interface BookingSuccessProps {
  bookingData?: {
    eventTimeId: string;
    neighborhoodId: string;
    specialRequests?: string;
  };
  paymentAmount: number;
  paymentCurrency: string;
  onViewDashboard: () => void;
  onBookAnother: () => void;
}

/**
 * The screen a booker lands on the moment the seat is theirs.
 *
 * Rewritten to the signup flow's visual language (components/onboarding), for
 * a plain reason: this component is not only a modal any more — PlansScreen
 * renders it full-bleed as the last beat of onboarding, so it sits directly
 * against those screens and used to look like it came from a different
 * product. It painted three saturated radial blobs behind itself, a 96px
 * green tick with a ring pulsing under it forever, a Poppins headline in
 * `secondary-800` with a party emoji after it, and a green that exists
 * nowhere else in the palette.
 *
 * There is no success mark at the top of it. A tick in a disc is what every
 * checkout in the world puts there, it says the same thing the headline
 * already says, and on the one screen that should feel like being let in it
 * made the page read as a receipt. The warmth comes from the bloom behind the
 * headline and from the confetti; the structure comes from the copy.
 *
 * The three "what happens next" lines are a timeline rather than three boxes.
 * They are three moments in a sequence, and a row of identical grey cards
 * states them as a feature list.
 *
 * Every entrance collapses to a plain fade under `prefers-reduced-motion`,
 * confetti included — the screen was previously the most motion-heavy in the
 * app and honoured the setting nowhere.
 */
export function BookingSuccess({
  onViewDashboard,
  onBookAnother,
}: BookingSuccessProps) {
  const { t } = useT();
  const [showConfetti, setShowConfetti] = useState(true);
  const shouldPitchInstall = useShouldPitchAppInstall();
  const askForPush = useAskForPushPermission();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), CONFETTI_LIFETIME_MS);
    return () => clearTimeout(timer);
  }, []);

  // Ask for notification permission so we can send reminders. The policy —
  // is it worth asking, and has anything asked already this session — lives in
  // the hook; this screen only decides when. 1.5s lets the success message
  // land before the sheet covers it.
  useEffect(() => {
    const tid = setTimeout(() => void askForPush(), 1500);
    return () => clearTimeout(tid);
  }, [askForPush]);

  /**
   * One entrance, parameterised by when. Reduced motion keeps the stagger —
   * the order things arrive in is information — and drops the travel.
   */
  const rise = (delay: number) =>
    reduceMotion
      ? {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { delay: delay * 0.5, duration: 0.2 },
        }
      : {
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.35 },
        };

  return (
    // The signup shell's canvas, to the literal value: this renders inside it
    // on the onboarding route, and anything else there reads as a card sitting
    // on a page rather than as the page. Opaque, because the other placement
    // is a modal over a dimmed backdrop.
    <div className="relative w-full max-h-[95vh] h-[100%] overflow-auto bg-white dark:bg-[#0C0B10]">
      {showConfetti && !reduceMotion && <Confetti />}

      {/* The warmth, in place of the tick that used to sit above the headline.
          Pinned to the card's bottom-right corner rather than to the content
          column, so it reads as light coming into the room from off-screen
          instead of as a halo attached to a paragraph.

          One flash, not a loop: anything that keeps pulsing reads as a
          notification badge demanding something, on the one screen where
          nothing is being asked of anybody. Drawn rather than blurred — see
          onboarding/glow.ts.

          Its own clipping layer, because the glow hangs past the corner and
          this container is the scroller: an unclipped overflow would give the
          card a scrollbar for the sake of a decoration. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <motion.div
          className="absolute -bottom-24 -right-24 w-[380px] h-[380px] rounded-full"
          style={{ background: softGlow('239,98,37', 0.3) }}
          initial={{ opacity: 0, scale: 0.75 }}
          animate={
            reduceMotion
              ? { opacity: 0.5, scale: 1 }
              : { opacity: [0, 1, 0.55], scale: [0.75, 1.08, 1] }
          }
          transition={{ duration: 1.6, times: [0, 0.35, 1], ease: 'easeOut' }}
        />
      </div>

      {/* A reading measure. The booking modal is `max-w-4xl`, where full-width
          body text ran to well over 100 characters a line. */}
      <div className="relative z-10 mx-auto w-full max-w-[520px] px-5 sm:px-6 py-7 sm:py-9">
        {/* The headline opens the screen. No emoji: the confetti is the
            celebration, and a party popper after a headline says the same
            thing a second time in a voice the rest of the product doesn't
            use. */}
        <motion.h1
          {...rise(0.05)}
          className="relative font-display font-semibold text-[32px] leading-[1.14] tracking-tight
            text-neutral-900 dark:text-neutral-50"
        >
          {t('booking.success.title')}
        </motion.h1>
        <motion.p
          {...rise(0.12)}
          className="relative text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-2.5"
        >
          {t('booking.success.subtitle')}
        </motion.p>

        {/* What happens next, as a timeline. */}
        <motion.h2
          {...rise(0.2)}
          className="relative font-display font-medium text-[15px] leading-snug
            text-neutral-500 dark:text-neutral-400 mt-7"
        >
          {t('booking.success.whatsNext')}
        </motion.h2>
        <ol className="relative mt-4">
          {STEPS.map((key, i) => (
            <motion.li
              key={key}
              {...rise(0.26 + i * 0.07)}
              className="relative flex gap-4 pb-6 last:pb-0"
            >
              {/* The rail, drawn dot-centre to dot-centre so the beats are
                  strung on one continuous line rather than each carrying a
                  stub of its own. It has to hang off this `li` — an absolute
                  child resolves against the padding box, which is what lets it
                  cross the `pb-5` gutter and reach the next dot. Positioned
                  against the marker column instead (where it started) it
                  stopped at the text's baseline and the line read as four
                  disconnected ticks.

                  Warm, not a cool grey: this hairline sits in a palette of
                  orange and cream, and black-at-low-alpha reads as a borrowed
                  component. Same value as the flow's row edge. */}
              {i < STEPS.length - 1 && (
                <span
                  aria-hidden
                  className="absolute left-0 top-3 -bottom-3 w-2.5 flex justify-center"
                >
                  <span className="w-px h-full bg-[#E7E0DA] dark:bg-white/10" />
                </span>
              )}
              {/* Painted after the rail, so each dot caps the line it sits
                  on. */}
              <span aria-hidden className="relative flex-none w-2.5">
                <span className="absolute left-1/2 -translate-x-1/2 top-[7px] w-2.5 h-2.5 rounded-full bg-primary-500" />
              </span>
              {/* Title and body at the flow's own two-line row scale
                  (HowItWorksScreen), so a beat here reads as the same kind of
                  thing it read as during signup. */}
              <span className="flex-1 min-w-0">
                <span className="block font-medium text-[15px] leading-snug text-neutral-900 dark:text-neutral-50">
                  {t(`booking.success.steps.${key}.title`)}
                </span>
                <span className="block text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-1">
                  {t(`booking.success.steps.${key}.body`)}
                </span>
              </span>
            </motion.li>
          ))}
        </ol>

        {/* Install nudge — highest-intent moment there is, and a web-only
            booker would otherwise miss every push reminder before the night.
            The one boxed thing on the screen, which is what makes it read as
            an aside rather than as the next paragraph. */}
        {shouldPitchInstall && (
          <motion.div {...rise(0.5)} className="relative mt-7">
            <InstallAppPitch
              source="booking_success"
              className={`${ROW_BORDER} ${ROW_SURFACE} rounded-2xl p-5`}
              ctaVariant="secondary"
            />
          </motion.div>
        )}

        {/* Both actions outlined, like the store CTA above them.
            Nothing here is being sold and neither route is wrong — the seat is
            already booked — so a solid pill would be manufacturing a decision
            out of two equally fine exits. Stacked down a phone, next to the
            install CTA, it was also three filled buttons in a column.

            Emphasis comes from order instead: column-reverse on a phone puts
            the dashboard under the thumb, while DOM order is left-to-right
            desktop order, which is also the reading order. */}
        <motion.div
          {...rise(0.58)}
          className="relative flex flex-col-reverse sm:flex-row gap-3 mt-8"
        >
          <button
            onClick={onBookAnother}
            className="btn btn-secondary flex-1 py-3.5"
          >
            {t('booking.success.bookAnother')}
          </button>
          <button
            onClick={onViewDashboard}
            className="btn btn-secondary flex-1 py-3.5"
          >
            {t('booking.success.viewDashboard')}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
